import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import Axios from 'axios';
import moment from 'moment';

import { ChartType, useSnapshotChartData } from 'root/apps/BusinessSnapshot/useSnapshotChartData';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogRum } from 'root/datadog/datadogRum';

import { CapitalOfferTags } from '../enums';
import { useHasBannerRights } from './useHasBannerRights';

const OFFER_URL = '/admin/payments/lightspeed-payments/offer';

export interface UseOfferData {
  offerId?: string;
  offerAmount?: string;
  showOffer: boolean;
  setShowOffer: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  hasBannerRights: boolean;
  daysUntilExpiration?: number;
  expiryDate?: string;
  expiresAt?: string;
  chartType: ChartType;
  feeDiscountRate: number;
  feePercentage: number;
  maxAmount: number; // cents
  daysUntilExpiry: number;
  tags: string[];
  isBackbookOffer: boolean;
  offerCurrency?: string;
  maxFeeSavingsAmount: number; // cents
}

export const getDaysUntilExpiration = (expirationDate: string) =>
  Math.ceil(moment(expirationDate).diff(moment(), 'days', true));

export const useOfferData = (): UseOfferData => {
  const [offerId, setOfferId] = useState<string | undefined>(undefined);
  const [offerAmount, setOfferAmount] = useState<string | undefined>(undefined);
  const [showOffer, setShowOffer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [daysUntilExpiration, setDaysUntilExpiration] = useState<number | undefined>(undefined);
  const [expiryDate, setExpiryDate] = useState<string | undefined>(undefined);
  const [expiresAt, setExpiresAt] = useState<string | undefined>(undefined);
  const [feeDiscountRate, setFeeDiscountRate] = useState<number>(0);
  const [feePercentage, setFeePercentage] = useState<number>(0);
  const [maxAmount, setMaxAmount] = useState<number>(0);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState<number>(0);
  const [tags, setTags] = useState<string[]>([]);
  const [offerCurrency, setOfferCurrency] = useState<string | undefined>(undefined);
  const [maxFeeSavingsAmount, setMaxFeeSavingsAmount] = useState<number>(0);
  const hasBannerRights = useHasBannerRights();
  const hasChartRights = Modules.hasModule('reports.capital.inventory_experiment');
  const hasChartV2Rights = Modules.hasModule('reports.capital.inventory_experiment_v2');

  let chartType: ChartType = 'none';

  if (hasChartRights) chartType = 'costsVsSales';
  if (hasChartV2Rights) chartType = 'stockout';

  const {
    data: chartData,
    isLoading: isChartLoading,
    isError: isChartError,
  } = useSnapshotChartData(chartType);

  // For the chart type, we check for data and set to none to fall back to the banner
  if (!isChartLoading) {
    switch (chartType) {
      case 'costsVsSales':
        if (!chartData?.data?.chartData?.sale_to_cost_by_months?.length) chartType = 'none';
        break;
      case 'stockout':
        if (!chartData?.data?.chartData?.sale_to_missed_revenue?.length) chartType = 'none';
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    const getOffer = async () => {
      setIsLoading(true);

      try {
        const result = await Axios.get(OFFER_URL);
        const { data: offerData } = result.data;

        if (!Array.isArray(offerData) && !offerData.isHidden) {
          const formattedAmount = Intl.NumberFormat(navigator.language, {
            style: 'currency',
            currency: offerData.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(offerData.offerAmount);

          setOfferAmount(formattedAmount);
          setOfferId(offerData.UUID);
          setDaysUntilExpiration(getDaysUntilExpiration(offerData.expiresAt));
          setExpiresAt(offerData.expiresAt);
          setExpiryDate(
            Intl.DateTimeFormat(navigator.language, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }).format(new Date(offerData.expiresAt))
          );
          setFeeDiscountRate(offerData.feeDiscountRate ?? 0);
          setFeePercentage(offerData.feePercentage ?? 0);
          setMaxAmount(offerData.maxAmount ?? 0);
          setDaysUntilExpiry(offerData.daysUntilExpiry ?? 0);
          setTags(offerData.tags ?? []);
          setOfferCurrency(offerData.currency);
          setMaxFeeSavingsAmount(offerData.maxFeeSavingsAmount ?? 0);
          setShowOffer(true);
        }
      } catch (error) {
        datadogRum.addError(error, { message: 'Failed to retrieve an offer' });
        setShowOffer(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (hasBannerRights && chartType === 'none' && !isChartLoading) {
      getOffer();
    }
  }, [hasBannerRights, chartType, isChartLoading]);

  return {
    offerId,
    offerAmount,
    showOffer,
    setShowOffer,
    isLoading: isLoading || isChartLoading,
    hasBannerRights,
    daysUntilExpiration,
    expiryDate,
    expiresAt,
    chartType: isChartLoading || isChartError ? 'none' : chartType,
    feeDiscountRate,
    feePercentage,
    maxAmount,
    daysUntilExpiry,
    tags,
    isBackbookOffer: tags.includes(CapitalOfferTags.Backbook) && feeDiscountRate > 0,
    offerCurrency,
    maxFeeSavingsAmount,
  };
};
